import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Chip } from "@mui/material";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import Swal from "sweetalert2";
import { getDailySummary, approveShippingPayment, rejectShippingPayment, approveBranchExpense, rejectBranchExpense, approveServiceCharge, rejectServiceCharge } from "api/financial";
import { getMyDailySummary } from "api/branchCash";
import ShippingApprovalDetailDialog from "./ShippingApprovalDetailDialog";
import ExpenseDetailDialog from "./ExpenseDetailDialog";
import ServiceChargeDetailDialog from "./ServiceChargeDetailDialog";
import TransactionDetailDialog from "./TransactionDetailDialog";
import TopupDetailDialog from "./TopupDetailDialog";
import RejectReasonDialog from "./RejectReasonDialog";
import CustomerSearchAutocomplete from "components/CustomerSearchAutocomplete";

const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const money = (n, c = "THB") => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtT = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");
const fmtD = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "");
const todayStr = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD, local
const labelSx = { fontSize: "10.5px", letterSpacing: ".14em", color: "#a8a3b4", fontWeight: 700 };

function DateBox({ label, value, min, max, onChange }) {
  return (
    <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "11px", px: "14px", height: 38, display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 1px 2px rgba(20,18,30,.04)" }}>
      <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, color: "#a8a3b4", letterSpacing: ".08em", whiteSpace: "nowrap" }}>{label.toUpperCase()}</Box>
      <Box component="input" type="date" value={value} min={min} max={max} onChange={(e) => onChange(e.target.value)}
        sx={{ fontFamily: MONO, fontSize: 13, color: "#2c2937", border: "none", outline: "none", background: "transparent", cursor: "pointer" }} />
    </Box>
  );
}

function Kpi({ label, value, color, sub }) {
  return (
    <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "16px", p: "14px 16px", boxShadow: "0 1px 2px rgba(20,18,30,.04)" }}>
      <Box sx={labelSx}>{label.toUpperCase()}</Box>
      <Box sx={{ fontFamily: MONO, fontSize: 18, fontWeight: 700, color, mt: "6px" }}>{value}</Box>
      {sub && <Box sx={{ fontFamily: MONO, fontSize: 11, color: "#b3aebd", mt: "2px" }}>{sub}</Box>}
    </Box>
  );
}

const KIND_STYLE = {
  visit_paid: { bg: "#f0fdf4", border: "#86efac", col: "#15803d" },
  visit_zero: { bg: "#f4f2f8", border: "#e2ddef", col: "#8b8697" },
  visit_rejected: { bg: "#fdeef0", border: "#f4b9c0", col: "#c0343a" },
  expense: { bg: "#fdeef0", border: "#f4b9c0", col: "#c0343a" },
  expense_pending: { bg: "#fdf1de", border: "#f0cf9a", col: "#a85c00" },
  service_charge: { bg: "#f0fdf4", border: "#86efac", col: "#15803d" },
  bank: { bg: "#f3f0fc", border: "#ddd0fa", col: "#6d28d9" },
};

// One station + one day: KPI strip (customers/packages/collected/expenses/closing
// balance) + a single chronological feed mixing customer pickups (ShippingPayment)
// and cash movements (deposit-to-bank / adjustment / expense). Every row opens the
// matching detail dialog — see ExpenseDetailDialog / ShippingApprovalDetailDialog /
// TransactionDetailDialog. `mode="branch"` uses the station-scoped staff endpoint
// (read + record only); `mode="financial"` uses the manager endpoint and allows
// inline approve/reject on pending rows.
export default function DailySummary({ stationId, mode = "branch", refreshToken }) {
  const { t } = useTranslation();
  // Payment-line label for the row description below — lets staff see at a glance
  // how the fee was actually paid (cash vs. bank transfer), not just the total.
  const methodLabel = (m) => (m === "bank_transfer" ? t("pickup.payment.bankTransfer") : t("pickup.payment.cash"));
  // Fee/paid parts for a row — change lines are never included here, since each
  // one gets its own sub-row below the parent instead (see changeSubRows).
  const visitDetailParts = (v) => {
    // Fully wallet-paid (no cash/bank line at all): say so plainly, with the
    // wallet debit's own immutable before/after balance (batched onto each visit
    // by backend buildDailySummary) instead of "Paid now: ฿0", which reads as if
    // nothing was actually collected.
    const fullyPaidByWallet =
      (v.lines || []).length === 0 && (Number(v.walletPortionAmount || v.walletPortionTHB) || 0) > 0;
    const paidPart =
      fullyPaidByWallet && v.walletTxnBalanceBefore != null && v.walletTxnBalanceAfter != null
        ? {
            label: t("financial.paidByWallet"),
            value: `${t("financial.balanceBefore")} ${money(v.walletTxnBalanceBefore, v.walletCurrency || "THB")}; ${t(
              "financial.balanceAfter"
            )} ${money(v.walletTxnBalanceAfter, v.walletCurrency || "THB")}`,
          }
        : {
            label: t("pickup.payment.paidNow"),
            value: (v.lines || []).length > 0
              ? v.lines.map((l) => `${money(l.amount, l.currency)} ${methodLabel(l.method)}`).join(" + ")
              : money(0, "THB"),
          };
    return [{ label: t("pickup.payment.feeDue"), value: money(v.feeTHB, "THB") }, paidPart];
  };
  // Same idea as visitDetailParts, for a BranchCashServiceCharge — "Purpose"
  // instead of a fixed fee-due label, since the charge itself has no rate-table
  // price.
  const serviceChargeDetailParts = (sc) => {
    const fullyPaidByWallet =
      (sc.lines || []).length === 0 && (Number(sc.walletPortionAmount) || 0) > 0;
    const paidPart =
      fullyPaidByWallet && sc.walletTxnBalanceBefore != null && sc.walletTxnBalanceAfter != null
        ? {
            label: t("financial.paidByWallet"),
            value: `${t("financial.balanceBefore")} ${money(sc.walletTxnBalanceBefore, sc.walletCurrency || "THB")}; ${t(
              "financial.balanceAfter"
            )} ${money(sc.walletTxnBalanceAfter, sc.walletCurrency || "THB")}`,
          }
        : {
            label: t("pickup.payment.paidNow"),
            value: (sc.lines || []).length > 0
              ? sc.lines.map((l) => `${money(l.amount, l.currency)} ${methodLabel(l.method)}`).join(" + ")
              : money(0, "THB"),
          };
    return [{ label: t("branchCash.serviceChargePurpose"), value: sc.purpose }, paidPart];
  };
  const settledTopupDetailParts = (tup) => {
    return [
      { label: t("branchCash.topUp"), value: money(tup.amount, tup.currency) },
      { label: t("pickup.payment.paidNow"), value: money(tup.receivedAmount, tup.receivedCurrency) },
    ];
  };
  // Adjustment reason lives on the ledger leg's `remark` (see performBranchCashAdjust);
  // before/after are that same leg's own immutable balanceBefore/balanceAfter, not
  // today's live cache, so a past adjustment still reads correctly.
  const adjustmentDetailParts = (m) => [
    { label: t("financial.reasonHeader"), value: m.remark || "—" },
    { label: t("financial.balanceBefore"), value: money(m.balanceBefore, m.currency) },
    { label: t("financial.balanceAfter"), value: money(m.balanceAfter, m.currency) },
  ];
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [customerObj, setCustomerObj] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitId, setVisitId] = useState(null);
  const [expenseId, setExpenseId] = useState(null);
  const [serviceChargeId, setServiceChargeId] = useState(null);
  const [txId, setTxId] = useState(null);
  const [topupId, setTopupId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null); // { kind: 'visit'|'expense'|'service_charge', id }

  const isManager = mode === "financial";

  const load = useCallback(async () => {
    if (!stationId) return;
    setLoading(true);
    const params = { dateFrom, dateTo, customer: customerObj?.name || undefined };
    const res = isManager
      ? await getDailySummary(stationId, params)
      : await getMyDailySummary({ stationId, ...params });
    setData(res && res.success ? res.data : null);
    setLoading(false);
  }, [stationId, dateFrom, dateTo, customerObj, isManager]);
  // refreshToken is bumped by the caller after recording a movement elsewhere on
  // the page (deposit/expense/service charge/adjustment) — not itself a filter,
  // so it's not part of `load`'s own deps, just a trigger to rerun it here.
  useEffect(() => { load(); }, [load, refreshToken]);

  const toast = (res) => Swal.fire({ icon: res.success ? "success" : "error", text: res.message, toast: true, position: "top-right", timer: 2200, showConfirmButton: false });

  const onApproveVisit = async (id) => {
    setBusy(true);
    const res = await approveShippingPayment(id);
    setBusy(false);
    toast(res);
    if (res.success) setVisitId(null);
    load();
  };
  const onApproveExpense = async (id) => {
    setBusy(true);
    const res = await approveBranchExpense(id);
    setBusy(false);
    toast(res);
    if (res.success) setExpenseId(null);
    load();
  };
  const onApproveServiceCharge = async (id) => {
    setBusy(true);
    const res = await approveServiceCharge(id);
    setBusy(false);
    toast(res);
    if (res.success) setServiceChargeId(null);
    load();
  };
  const doReject = async (reason) => {
    if (!rejectTarget) return;
    setBusy(true);
    const res = rejectTarget.kind === "visit"
      ? await rejectShippingPayment(rejectTarget.id, reason || "")
      : rejectTarget.kind === "service_charge"
      ? await rejectServiceCharge(rejectTarget.id, reason || "")
      : await rejectBranchExpense(rejectTarget.id, reason || "");
    setBusy(false);
    toast(res);
    if (res.success) { setRejectTarget(null); setVisitId(null); setExpenseId(null); setServiceChargeId(null); }
    load();
  };

  if (!stationId) return null;

  const visits = (data?.visits || []).map((v) => ({
    key: `v-${v._id}`, createdAt: v.createdAt, kind: "visit", data: v,
  }));
  // A service charge is a "visit"-like event just like a ShippingPayment (it can
  // settle to several ledger legs — cash/bank/wallet/change — sharing one
  // reference), so it's its own row kind here too, not folded into cashMovements.
  const serviceCharges = (data?.serviceCharges || []).map((sc) => ({
    key: `sc-${sc._id}`, createdAt: sc.createdAt, kind: "serviceCharge", data: sc,
  }));
  const movements = (data?.cashMovements || []).map((m) => ({
    key: `c-${m.id || m._id}`, createdAt: m.createdAt, kind: m.kind, data: m,
  }));
  const timeline = [...visits, ...serviceCharges, ...movements].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const kpi = data?.kpi || { customers: 0, packages: 0, collectedTHB: 0, expenses: {}, closingBalance: {} };

  return (
    <Box sx={{ fontFamily: FONT }}>
      <Box sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "text.secondary", mb: "14px" }}>
        {t("branchCash.dailySummary")}
      </Box>

      {/* filter: date range + customer — narrows the feed below, replaces the old
          all-time "recent transactions" list (see also outstandingParcels-style
          filter bars elsewhere in this scene). */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", mb: "18px" }}>
        {/* Picking a "from" date jumps the whole view to that single day (moves
            "to" along with it) — the common case is "show me this one date", not
            building a range. Still extendable into a real range by then changing
            "to" separately. */}
        <DateBox label={t("financial.dateFrom")} value={dateFrom} max={dateTo || todayStr()} onChange={(v) => { setDateFrom(v); setDateTo(v); }} />
        <DateBox label={t("financial.dateTo")} value={dateTo} min={dateFrom} max={todayStr()} onChange={setDateTo} />
        <CustomerSearchAutocomplete
          value={customerObj}
          onChange={setCustomerObj}
          label={t("financial.customer")}
          size="small"
          sx={{ flex: "1 1 260px", minWidth: 220, maxWidth: 380 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}><CircularProgress color="secondary" size={26} /></Box>
      ) : (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3,1fr)" }, gap: "12px" }}>
            {["THB", "LAK", "USD"].map((cur) => (
              <Kpi
                key={cur}
                label={cur}
                value={money(kpi.closingBalance?.[cur], cur)}
                color="#6d28d9"
                sub={
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }} title={t("branchCash.openingBalance")}>
                    <ScheduleOutlined sx={{ fontSize: 12 }} />
                    {money(kpi.openingBalance?.[cur], cur)}
                  </Box>
                }
              />
            ))}
          </Box>

          <Box sx={{ mt: "20px" }}>
            {timeline.length === 0 ? (
              <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "18px", p: "34px", textAlign: "center", color: "#a8a3b4" }}>{t("financial.noData")}</Box>
            ) : (
              <Box sx={{ border: "1px solid #ece9f1", borderRadius: "16px", overflow: "hidden" }}>
                {timeline.map((row, i) => {
                  const isVisit = row.kind === "visit";
                  const isTopup = row.kind === "topup";
                  const isServiceCharge = row.kind === "serviceCharge";
                  // An approved cash top-up: its received-cash credit + any change-given
                  // debit(s) sharing the same reference, merged into one row by the
                  // backend (buildDailySummary) instead of shown as raw ledger legs.
                  const isSettledTopup = row.kind === "topup_settled";
                  const v = isVisit ? row.data : null;
                  const sc = isServiceCharge ? row.data : null;
                  const tup = isTopup || isSettledTopup ? row.data : null;
                  const m = !isVisit && !isTopup && !isSettledTopup && !isServiceCharge ? row.data : null;
                  const styleKey = isVisit
                    ? (v.status === "pending_approval" ? "expense_pending" : v.status === "rejected" ? "visit_rejected" : (v.feeTHB > 0 ? "visit_paid" : "visit_zero"))
                    : isServiceCharge
                    ? (sc.status === "rejected" ? "visit_rejected" : sc.status === "pending_approval" ? "expense_pending" : "service_charge")
                    : isTopup
                    ? "expense_pending"
                    : isSettledTopup
                    ? "bank"
                    : (row.kind === "expense_request" ? "expense_pending" : m.purpose === "expense" ? "expense" : "bank");
                  const s = KIND_STYLE[styleKey];
                  // When change was given back, the parent row shows what was actually
                  // received in cash/bank (gross) — not the net fee — since the change
                  // itself is already its own separate sub-row transaction below. Showing
                  // the net fee here would double-count the change (visible on the sub-row
                  // AND silently baked into this row), understating what hit the drawer.
                  const visitAmountTHB = isVisit && (v.changeLines || []).length > 0
                    ? (v.lines || []).reduce((s2, l) => s2 + (Number(l.amountTHB) || 0), 0)
                    : v?.feeTHB;
                  // Same idea as visitAmountTHB — gross collected (not net fee) whenever
                  // change was given back, so the sub-row below doesn't get double-counted.
                  const scAmountTHB = isServiceCharge && (sc.changeLines || []).length > 0
                    ? (sc.lines || []).reduce((s2, l) => s2 + (Number(l.amountTHB) || 0), 0)
                    : sc?.feeTHB;
                  // When every payment line shares one non-THB currency, the headline
                  // amount should read in what was actually handed over (e.g. ₭41,000),
                  // not its ฿-equivalent fee — the THB figure is still visible below in
                  // "Fee due". Falls back to THB whenever paid by wallet (no lines), in
                  // THB itself, or split across more than one currency (nothing single
                  // native to show). Summing raw `amount` across lines mirrors the gross
                  // (not net-of-change) semantics of visitAmountTHB/scAmountTHB above.
                  const nativePaidAmount = (lines) => {
                    const currencies = new Set((lines || []).map((l) => l.currency));
                    if (currencies.size !== 1) return null;
                    const [currency] = currencies;
                    if (currency === "THB") return null;
                    return { amount: (lines || []).reduce((s2, l) => s2 + (Number(l.amount) || 0), 0), currency };
                  };
                  const visitNative = isVisit ? nativePaidAmount(v.lines) : null;
                  const scNative = isServiceCharge ? nativePaidAmount(sc.lines) : null;
                  const amountStr = isVisit
                    ? (v.feeTHB > 0 ? `+${visitNative ? money(visitNative.amount, visitNative.currency) : money(visitAmountTHB, "THB")}` : "–")
                    : isServiceCharge
                    ? (sc.feeTHB > 0 ? `+${scNative ? money(scNative.amount, scNative.currency) : money(scAmountTHB, "THB")}` : "–")
                    : isTopup
                    ? `+${money(tup.receivedAmount || tup.amount, tup.receivedCurrency || tup.currency)}`
                    : isSettledTopup
                    ? `+${money(tup.receivedAmount || tup.amount, tup.receivedCurrency || tup.currency)}`
                    : `${m.txn_type === "credit" ? "+" : "−"}${money(m.amount, m.currency)}`;
                  // A visit's/service charge's fee still counts toward "Collected today"
                  // regardless of how it was paid — only the amount COLOR distinguishes
                  // physical cash that actually landed in this station's drawer (green)
                  // from a method that never touches it, e.g. bank transfer straight to
                  // the company account, or a fee fully auto-charged to the customer's
                  // wallet (gray).
                  const visitHasCash = isVisit && (v.lines || []).some((l) => l.method === "cash");
                  const scHasCash = isServiceCharge && (sc.lines || []).some((l) => l.method === "cash");
                  // Not yet approved — nothing has actually moved in the drawer, so the
                  // amount reads orange like the pending pill above it instead of the
                  // green/red a settled movement gets (same reasoning as excluding it
                  // from the KPI totals above, which the backend already does).
                  const isPending = isTopup || (isVisit && v.status === "pending_approval") || (isServiceCharge && sc.status === "pending_approval") || row.kind === "expense_request";
                  const amountColor = isPending
                    ? "#a85c00"
                    : amountStr.startsWith("+")
                    ? ((isVisit && v.feeTHB > 0 && !visitHasCash) || (isServiceCharge && sc.feeTHB > 0 && !scHasCash) ? "#6b6775" : "#117a4f")
                    : amountStr === "–"
                    ? "#b3aebd"
                    : "#c0343a";
                  const title = isVisit
                    ? (v.customer?.name || t("financial.customer"))
                    : isServiceCharge
                    ? (sc.customer?.name || sc.purpose)
                    : isTopup || isSettledTopup
                    ? (tup.customer?.name || t("financial.customer"))
                    : row.kind === "expense_request"
                    ? m.reason
                    : m.purpose === "expense"
                    ? m.remark
                    : m.purpose === "deposit" || m.purpose === "refund"
                    ? (m.customer?.name || t("financial.customer"))
                    : m.purpose;
                  const tag = isVisit
                    ? `${t("branchCash.pickup")} · ${(v.packageIds || []).length}`
                    : isServiceCharge
                    ? (sc.status === "pending_approval" ? `${t("branchCash.serviceCharge")} · ${t("financial.pending")}` : t("branchCash.serviceCharge"))
                    : isTopup
                    ? `${t("branchCash.topUp")} · ${t("financial.pending")}`
                    : isSettledTopup
                    ? t("branchCash.topUp")
                    : row.kind === "expense_request"
                    ? t("financial.pending")
                    : m.purpose === "expense"
                    ? t("branchCash.expense")
                    : m.purpose === "deposit"
                    ? t("branchCash.topUp")
                    : m.purpose === "refund"
                    ? t("branchCash.changeGiven")
                    : m.purpose;
                  // No detail dialog for a pending topup row yet — the secondary line
                  // already surfaces who requested it, so it isn't clickable. A settled
                  // (approved) top-up opens the dedicated TopupDetailDialog (keyed to the
                  // WalletTopupRequest, not one leg) — except a legacy record that predates
                  // WalletTopupRequest.reference, which has no topupRequestId to open that
                  // with, so it falls back to the generic per-leg TransactionDetailDialog.
                  const onRowClick = () => {
                    if (isVisit) setVisitId(v._id);
                    else if (isServiceCharge) setServiceChargeId(sc._id);
                    else if (isTopup) return;
                    else if (row.kind === "expense_request") setExpenseId(m._id);
                    else if (isSettledTopup) (tup.topupRequestId ? setTopupId(tup.topupRequestId) : setTxId(tup.id));
                    else setTxId(m.id);
                  };
                  // Each change line gets its own sub-row below the parent (instead of
                  // folding into the description text) so staff can see it the same way
                  // any other feed row reads: who, what, how much. Sign follows where the
                  // money actually moves: a wallet-credit line deposits into the
                  // customer's wallet ("+"); a cash line withdraws physical cash from
                  // this station's drawer to hand back ("-").
                  const changeSubRows = (isVisit || isServiceCharge || isSettledTopup)
                    ? (isVisit ? v : isServiceCharge ? sc : tup).changeLines || []
                    : [];
                  // Clean THB change-due figure for a visit/service charge — paidNow minus
                  // fee, computed directly rather than reverse-converting a change line's
                  // amountTHB, which carries LAK-note-rounding noise (change handed back in
                  // physical 1,000-LAK notes, e.g. ₭35,000 back-converts to ฿50.72 instead
                  // of the ฿50 actually owed — see the LAK tolerance in
                  // collectShippingPayment/recordServiceCharge). Only valid when there's
                  // exactly one change line; a split across several lines can't be cleanly
                  // re-derived this way.
                  const visitChangeDueTHB = (isVisit || isServiceCharge) && changeSubRows.length === 1
                    ? ((isVisit ? v : sc).lines || []).reduce((s, l) => s + (Number(l.amountTHB) || 0), 0) - (Number((isVisit ? v : sc).feeTHB) || 0)
                    : null;
                  return (
                    <Box key={row.key}>
                      <Box onClick={onRowClick}
                        sx={{ display: "flex", alignItems: "center", gap: "14px", p: "13px 18px", borderTop: i === 0 ? "none" : "1px solid #f4f2f8", cursor: isTopup ? "default" : "pointer", "&:hover": isTopup ? {} : { background: "#faf9fc" } }}>
                        <Box sx={{ width: 58, flex: "none" }}>
                          <Box sx={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, color: "#7c7787" }}>{fmtD(row.createdAt)}</Box>
                          <Box sx={{ fontFamily: MONO, fontSize: 10.5, color: "#b3aebd", mt: "1px" }}>{fmtT(row.createdAt)}</Box>
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <Box sx={{ fontSize: 13.5, fontWeight: 700, color: "#2c2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>{title || "—"}</Box>
                            <Chip size="small" label={tag} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: s.bg, color: s.col, border: `1px solid ${s.border}` }} />
                          </Box>
                          {!isVisit && !isServiceCharge && !isTopup && !isSettledTopup && (
                            <Box sx={{ fontSize: 11.5, color: "#9b96a6", mt: "2px" }}>
                              {row.kind === "expense_request"
                                ? `${t("branchCash.requestedBy")}: ${m.requestedBy?.name || "—"}`
                                : m.purpose === "deposit" || m.purpose === "refund"
                                ? (m.description || "—")
                                : m.purpose === "adjustment"
                                ? adjustmentDetailParts(m).map((p) => `${p.label}: ${p.value}`).join(", ")
                                : `${t("branchCash.requestedBy")}: ${m.actionBy?.name || "—"}`}
                            </Box>
                          )}
                          {isTopup && (
                            <Box sx={{ fontSize: 11.5, color: "#9b96a6", mt: "2px" }}>
                              {tup.changeLines?.length > 0
                                ? `${t("wallet.admin.giveChange")}: ${tup.changeLines.map((l) => money(l.amount, l.currency)).join(" + ")}`
                                : `${t("branchCash.requestedBy")}: ${tup.createdByStaff?.name || "—"}`}
                            </Box>
                          )}
                          {isSettledTopup && (
                            <Box sx={{ fontSize: 11.5, color: "#9b96a6", mt: "2px" }}>
                              {/* Change is its own sub-row below, so excluded here. */}
                              {settledTopupDetailParts(tup, false).map((p) => `${p.label}: ${p.value}`).join(", ")}
                            </Box>
                          )}
                          {isVisit && (
                            <Box sx={{ fontSize: 11.5, color: "#9b96a6", mt: "2px" }}>
                              {visitDetailParts(v, false).map((p) => `${p.label}: ${p.value}`).join(", ")}
                            </Box>
                          )}
                          {isServiceCharge && (
                            <Box sx={{ fontSize: 11.5, color: "#9b96a6", mt: "2px" }}>
                              {serviceChargeDetailParts(sc).map((p) => `${p.label}: ${p.value}`).join(", ")}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ fontFamily: MONO, fontSize: 14.5, fontWeight: 700, color: amountColor, whiteSpace: "nowrap" }}>
                          {amountStr}
                        </Box>
                      </Box>
                      {changeSubRows.map((cl, ci) => {
                        const isWalletCredit = cl.source === "wallet_credit";
                        const subAmount = `${isWalletCredit ? "+" : "-"}${money(cl.amount, cl.currency)}`;
                        // Cash change: phrased as a THB-denominated instruction ("give ฿X
                        // back") since that's the figure staff actually owe the customer —
                        // visitChangeDueTHB when clean (see above), else cl.amountTHB — plus
                        // the exchange rate used whenever it was converted to/from another
                        // currency. Wallet credit keeps the plain "{customer} — Wallet
                        // credit" label.
                        const cashAmountTHB = visitChangeDueTHB ?? cl.amountTHB ?? cl.amount;
                        const subLabel = isWalletCredit
                          ? `${title} — ${t("pickup.payment.changeWallet")}`
                          : cl.currency !== "THB" && cl.exchangeRate
                          ? t("pickup.payment.giveChangeCashRate", { amount: money(cashAmountTHB, "THB"), rate: cl.exchangeRate })
                          : t("pickup.payment.giveChangeCash", { amount: money(cashAmountTHB, "THB") });
                        // Wallet credit is gray, same as a bank-transfer/wallet-paid fee
                        // above — it doesn't touch this station's physical cash drawer
                        // either. Cash change stays purple/red since it's a real
                        // withdrawal from the drawer.
                        return (
                          <Box key={`${row.key}-cl-${ci}`} onClick={onRowClick}
                            sx={{ display: "flex", alignItems: "center", gap: "14px", pl: "78px", pr: "18px", py: "9px", borderTop: "1px dashed #ece9f1", background: "#fbfaff", cursor: "pointer", "&:hover": { background: "#f4f2f8" } }}>
                            <Box sx={{ minWidth: 0, flex: 1, fontSize: 12.5, fontWeight: 600, color: isWalletCredit ? "#6b6775" : "#6d28d9" }}>
                              {subLabel}
                            </Box>
                            <Box sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: isWalletCredit ? "#6b6775" : "#c0343a", whiteSpace: "nowrap" }}>
                              {subAmount}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </>
      )}

      <ShippingApprovalDetailDialog
        open={!!visitId}
        paymentId={visitId}
        busy={busy}
        onClose={() => setVisitId(null)}
        onApprove={isManager ? onApproveVisit : undefined}
        onReject={isManager ? (id) => setRejectTarget({ kind: "visit", id }) : undefined}
      />
      <ExpenseDetailDialog
        open={!!expenseId}
        expenseId={expenseId}
        mode={mode}
        busy={busy}
        onClose={() => setExpenseId(null)}
        onApprove={isManager ? onApproveExpense : undefined}
        onReject={isManager ? (id) => setRejectTarget({ kind: "expense", id }) : undefined}
      />
      <ServiceChargeDetailDialog
        open={!!serviceChargeId}
        serviceChargeId={serviceChargeId}
        mode={mode}
        busy={busy}
        onClose={() => setServiceChargeId(null)}
        onApprove={isManager ? onApproveServiceCharge : undefined}
        onReject={isManager ? (id) => setRejectTarget({ kind: "service_charge", id }) : undefined}
      />
      <TransactionDetailDialog mode={mode} open={!!txId} txId={txId} onClose={() => setTxId(null)} />
      <TopupDetailDialog mode={mode} open={!!topupId} topupId={topupId} onClose={() => setTopupId(null)} />
      <RejectReasonDialog open={!!rejectTarget} busy={busy} onClose={() => setRejectTarget(null)} onConfirm={doReject} />
    </Box>
  );
}
